import { useCallback, useMemo } from 'react'
import { GridRowParams } from '@mui/x-data-grid-premium'
import { SchedulerTableDataType, SchedulerTableProps } from './Scheduler.types'
import {
  canModifySchedule,
  canOpenInput,
  canOpenOutput,
} from './scheduleStatus'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { ActionItem } from 'Common/Components/MUITableAction'
import InputIcon from '@mui/icons-material/Input'
import OutputIcon from '@mui/icons-material/Output'
import { PLAN_ACCESS_TYPE } from 'Common/Constants/Constants'
import {
  CancelOutlined,
  Check,
  ContentCopy,
  DeleteForever,
  DriveFileRenameOutline,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Pause,
} from '@mui/icons-material'
import { FONT_SANS, SchChip, TOKENS } from './SchedulerDesign'
/** A Date, or null when the value does not parse. */
const validDate = (value?: string | null): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * When the schedule was created. `generatedAt` (date and time) where the
 * service provides it, else the dd/mm/yyyy `generatedOn` older rows stored,
 * which `new Date` would read as mm/dd.
 */
const generatedDate = (row: SchedulerTableDataType): Date | null => {
  const exact = validDate(row.generatedAt)
  if (exact) return exact
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(row.generatedOn ?? '')
  if (!match) return null
  const [, day, month, year] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

export const SchedulerTable = ({
  data,
  isLoading,
  handleRowClick,
  handleInputClick,
  handleOutputClick,
  userEmail,
  handleAccessTypeChange,
  handleDelete,
  handleRename,
  handleClone,
}: SchedulerTableProps) => {
  const getStatus = useCallback(({ status }: SchedulerTableDataType) => {
    switch (status) {
      case 'SUCCESS':
        return (
          <SchChip tone="complete" icon={<Check sx={{ fontSize: 13 }} />}>
            {status}
          </SchChip>
        )
      case 'RUNNING':
        return (
          <SchChip tone="running" icon={<CircularProgress size={10} />}>
            {status}
          </SchChip>
        )
      case 'FAILED':
        return (
          <SchChip
            tone="failed"
            icon={<CancelOutlined sx={{ fontSize: 13 }} />}
          >
            {status}
          </SchChip>
        )
      case 'INITIATED':
        return (
          <SchChip tone="running" icon={<CircularProgress size={10} />}>
            {status}...
          </SchChip>
        )
      case 'UNSTARTED':
        return (
          <SchChip tone="neutral" icon={<Pause sx={{ fontSize: 13 }} />}>
            {status}
          </SchChip>
        )
      default:
        return <SchChip tone="neutral">{status}</SchChip>
    }
  }, [])

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        type: 'string',
        renderCell: (params) => {
          const { row } = params
          return (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              minWidth="100%"
            >
              <Typography
                noWrap
                sx={{
                  fontFamily: FONT_SANS,
                  fontSize: 13,
                  fontWeight: 600,
                  color: TOKENS.ink900,
                }}
              >
                {row.name}
              </Typography>
              {row?.generatedBy === userEmail && (
                <Box display="flex">
                  {row?.scheduleAccess?.scheduleAccessType ===
                    PLAN_ACCESS_TYPE.PRIVATE && (
                    <Box>
                      <IconButton size="small">
                        <Tooltip title="This schedule is locked.">
                          <LockIcon
                            sx={{ fontSize: 15, color: TOKENS.ink400 }}
                          />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )
        },
      },
      // Both date columns hold real Date values, so they sort by time; the
      // formatter only decides what is shown. They used to be strings, which
      // sorted dd/mm/yyyy by day of the month.
      {
        headerName: 'Generated on',
        field: 'generatedOn',
        type: 'dateTime',
        flex: 1,
        cellClassName: 'sch-mono',
        valueGetter: (_, row) => generatedDate(row),
        valueFormatter: (value: Date | null, row) =>
          value
            ? row.generatedAt
              ? value.toLocaleString(undefined, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })
              : value.toLocaleDateString()
            : '',
      },
      {
        headerName: 'Start date',
        field: 'startDate',
        type: 'date',
        flex: 1,
        cellClassName: 'sch-mono',
        valueGetter: (_, row) => validDate(row.scheduleDetails?.start_date),
        valueFormatter: (value: Date | null) =>
          value ? value.toLocaleDateString() : '',
      },
      {
        headerName: 'Status',
        field: 'status',
        type: 'string',
        flex: 1,
        renderCell: (params) => getStatus(params.row),
      },
      {
        headerName: 'Generated by',
        field: 'generatedBy',
        type: 'string',
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams) => {
          return [
            <ActionItem
              clickHandler={() => handleInputClick(params)}
              key="Input"
              label="Input"
              showInMenu
              icon={<InputIcon />}
              closeMenuOnClick={true}
              disabled={!canOpenInput(params.row.status)}
            />,
            <ActionItem
              clickHandler={() => handleOutputClick(params)}
              key="Output"
              label="Output"
              showInMenu
              icon={<OutputIcon />}
              closeMenuOnClick={true}
              disabled={!canOpenOutput(params.row.status)}
            />,
            <ActionItem
              clickHandler={() => handleDelete(params)}
              key="Delete"
              label="Delete"
              className="sch-menu-danger"
              showInMenu
              icon={<DeleteForever />}
              closeMenuOnClick={true}
              disabled={!canModifySchedule(params.row.status)}
            />,
            <ActionItem
              clickHandler={() => handleAccessTypeChange(params)}
              key="Lock"
              label={
                params.row?.scheduleAccess?.scheduleAccessType ===
                PLAN_ACCESS_TYPE.PRIVATE
                  ? 'Make public'
                  : 'Make private'
              }
              showInMenu
              icon={
                params.row?.scheduleAccess?.scheduleAccessType ===
                PLAN_ACCESS_TYPE.PRIVATE ? (
                  <LockOpenIcon />
                ) : (
                  <LockIcon />
                )
              }
              closeMenuOnClick={true}
              disabled={
                params.row.generatedBy !== userEmail ||
                !canModifySchedule(params.row.status)
              }
            />,
            <ActionItem
              clickHandler={() => handleRename(params)}
              key="Rename"
              label="Rename"
              showInMenu
              icon={<DriveFileRenameOutline />}
              closeMenuOnClick={true}
              // Owner only, as for access changes; the service enforces it.
              disabled={
                params.row.generatedBy !== userEmail ||
                !canModifySchedule(params.row.status)
              }
            />,
            <ActionItem
              clickHandler={() => handleClone(params)}
              key="Clone"
              label="Clone"
              showInMenu
              icon={<ContentCopy />}
              closeMenuOnClick={true}
              // Not mid-run: the copy would catch the run half-written.
              disabled={!canModifySchedule(params.row.status)}
            />,
          ]
        },
      },
    ],
    [userEmail]
  )

  return (
    <Box sx={{ width: '100%' }}>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={() => {}} // Read-only table
        uploadData={() => {}} // No upload functionality
        isUploading={false}
        isSaving={false}
        checkBoxSelection={false}
        showTableToolBar={false}
        downloadFileName="Scheduler"
        handleRowClick={handleRowClick}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{ width: '100%' }}
      />
    </Box>
  )
}
